import React from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle, Instagram, MapPin, Clock } from 'lucide-react';
import { businessInfo } from '../../data/mockData';
import { getDefaultInquiryURL } from '../../utils/whatsappGenerator';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="relative z-[10] overflow-hidden bg-[#F7F2EE]">
      <div className="section-divider-rose" aria-hidden="true" />
      <div className="pointer-events-none absolute -left-24 top-12 h-72 w-72 rounded-full bg-rose-200/30 blur-3xl" />
      <div className="pointer-events-none absolute -right-24 bottom-10 h-80 w-80 rounded-full bg-[#F5BCB1]/26 blur-3xl" />
      <div className="relative z-[20] max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-4">
              <img 
                src="/images/footer_logo.png?v=2" 
                alt="FlowBerry" 
                className="h-10 w-auto object-contain md:h-14 lg:h-18"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </Link>
            <p className="text-stone-600 text-sm font-medium leading-relaxed mb-4">
              Premium roses delivery in Los Angeles. Freshly arranged bouquets crafted with love in Glendale.
            </p>
            <div className="flex items-center gap-4">
              <a
                href={getDefaultInquiryURL()}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-rose-200/70 bg-white/70 text-[#FB7185] shadow-[0_10px_24px_rgba(120,72,54,0.10)] transition-all duration-300 hover:border-[#FB7185]/50 hover:bg-white/88 hover:shadow-[#FB7185]/15"
                aria-label="WhatsApp"
              >
                <MessageCircle className="w-5 h-5" />
              </a>
              <a
                href={businessInfo.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-10 w-10 items-center justify-center rounded-full border border-rose-200/70 bg-white/70 text-[#FB7185] shadow-[0_10px_24px_rgba(120,72,54,0.10)] transition-all duration-300 hover:border-[#FB7185]/50 hover:bg-white/88 hover:shadow-[#FB7185]/15"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-serif text-[#3A2A2A] font-bold mb-4">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/build" className="text-sm font-medium text-stone-600 hover:text-[#FB7185] transition-colors">
                  Build Your Bouquet
                </Link>
              </li>
              <li>
                <Link to="/delivery" className="text-sm font-medium text-stone-600 hover:text-[#FB7185] transition-colors">
                  Delivery & Payment
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-sm font-medium text-stone-600 hover:text-[#FB7185] transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-sm font-medium text-stone-600 hover:text-[#FB7185] transition-colors">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Service Areas */}
          <div>
            <h4 className="font-serif text-[#3A2A2A] font-bold mb-4">Service Areas</h4>
            <ul className="space-y-3 text-sm font-medium text-stone-600">
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#FB7185]" />
                <span>Glendale (Pickup available)</span>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#FB7185]" />
                <span>Los Angeles & LA County</span>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#FB7185]" />
                <span>Delivery from $10</span>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-serif text-[#3A2A2A] font-bold mb-4">Contact</h4>
            <ul className="space-y-3 text-sm font-medium text-stone-600">
              <li className="flex items-start gap-2">
                <MessageCircle className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#FB7185]" />
                <a 
                  href={getDefaultInquiryURL()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[#FB7185] transition-colors"
                >
                  WhatsApp: {businessInfo.whatsappNumber}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Clock className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#FB7185]" />
                <span>{businessInfo.workingHours}</span>
              </li>
              <li className="flex items-start gap-2">
                <Instagram className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#FB7185]" />
                <a 
                  href={businessInfo.instagramUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-[#FB7185] transition-colors"
                >
                  @flowberry_los
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-rose-200/70">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-stone-500">
              © {currentYear} FlowBerry. Premium roses, crafted in Glendale, CA.
            </p>
            <p className="text-sm text-stone-500">
              Payment: Zelle & Apple Cash
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
